import React, { useState } from 'react'
import managementService from '../../services/managementService'
import ManagementReportShell from './ManagementReportShell'
import useManagementReport from './useManagementReport'
import { DataTable, DrillLinks, HealthBadge, KpiGrid, KpiTile, Section } from './ManagementWidgets'
import { at, exportToSpreadsheet, flattenMetrics, isMetric } from './managementUtils'

const LINKS = [
  { label: 'Resource allocations', to: '/projects/resource-allocations' },
  { label: 'Resource calendar', to: '/projects/resource-calendar' },
  { label: 'Timesheet reports', to: '/timesheets?tab=reports' }
]

const BAND_TONE = { Bench: 'Grey', 'Over-allocated': 'Red', 'Under-allocated': 'Amber', Optimal: 'Green' }
const BANDS = ['All', 'Over-allocated', 'Under-allocated', 'Bench', 'Optimal']

export const ResourceCostTiles = ({ resourceCost }) => (
  isMetric(resourceCost)
    ? <KpiTile label="Resource cost" metric={resourceCost} format="currency" />
    : (
      <>
        <KpiTile label="Resource cost (estimated)" metric={resourceCost?.estimated} format="currency" hint={resourceCost?.note} />
        <KpiTile label="Resource cost (actual)" metric={resourceCost?.actual} format="currency" />
      </>
    )
)

export const EMPLOYEE_COLUMNS = [
  { key: 'employeeName', label: 'Employee' },
  { key: 'department', label: 'Department' },
  { key: 'designation', label: 'Designation' },
  { key: 'band', label: 'Band', render: (v) => <HealthBadge status={BAND_TONE[v]} label={v} /> },
  { key: 'allocationPercent', label: 'Allocation %', format: 'percent' },
  { key: 'billableAllocationPercent', label: 'Billable alloc %', format: 'percent' },
  { key: 'projectCount', label: 'Projects', format: 'number' },
  { key: 'hoursLogged', label: 'Hours', format: 'hours' },
  { key: 'utilizationPercent', label: 'Utilisation', format: 'percent' }
]

const ResourceCapacityReport = () => {
  const report = useManagementReport(managementService.getResourceCapacity)
  const [band, setBand] = useState('All')

  const onExport = (data) => exportToSpreadsheet('resource-capacity', {
    Summary: flattenMetrics({ summary: data.summary, resourceCost: data.resourceCost }),
    Departments: data.byDepartment || [],
    Employees: data.employees || []
  })

  return (
    <ManagementReportShell
      title="Resource Capacity"
      subtitle="Allocation bands, bench, utilisation and timesheet compliance"
      report={report}
      onExport={onExport}
    >
      {(d) => {
        const s = d.summary || {}
        const basis = d.capacityBasis || {}
        const employees = (d.employees || []).filter((e) => band === 'All' || e.band === band)
        return (
          <>
            <DrillLinks links={LINKS} />
            <Section
              title="Capacity summary"
              subtitle={basis.workingDays != null
                ? `${basis.workingDays} working days × ${basis.hoursPerDay} h per person · time source: ${basis.timeSource}`
                : undefined}
            >
              <KpiGrid>
                <KpiTile label="Headcount" metric={s.headcount} />
                <KpiTile label="Allocated" metric={s.allocatedEmployees} />
                <KpiTile label="Bench" metric={s.benchEmployees} tone="amber" />
                <KpiTile label="Over-allocated" metric={s.overAllocated} tone="red" />
                <KpiTile label="Under-allocated" metric={s.underAllocated} tone="amber" />
                <KpiTile label="Avg allocation" metric={s.averageAllocationPercent} format="percent" />
                <KpiTile label="Capacity hours" metric={s.capacityHours} format="hours" />
                <KpiTile label="Hours logged" metric={s.hoursLogged} format="hours" />
                <KpiTile label="Billable hours" metric={s.billableHours} format="hours" />
                <KpiTile label="Utilisation" metric={s.utilizationPercent} format="percent" />
                <KpiTile label="Billable utilisation" metric={s.billableUtilizationPercent} format="percent" />
                <KpiTile label="Timesheet compliance" metric={s.timesheetCompliancePercent} format="percent" />
                <ResourceCostTiles resourceCost={at(d, 'resourceCost')} />
              </KpiGrid>
            </Section>

            <Section title="By department">
              <DataTable
                rows={d.byDepartment || []}
                emptyText="No active employees."
                columns={[
                  { key: 'department', label: 'Department' },
                  { key: 'headcount', label: 'Headcount', format: 'number' },
                  { key: 'benchEmployees', label: 'Bench', format: 'number' },
                  { key: 'averageAllocationPercent', label: 'Avg allocation', format: 'percent' },
                  { key: 'hoursLogged', label: 'Hours', format: 'hours' },
                  { key: 'utilizationPercent', label: 'Utilisation', format: 'percent' }
                ]}
              />
            </Section>

            <Section
              title="Employees"
              actions={(
                <div className="mis-segmented no-print">
                  {BANDS.map((b) => (
                    <button key={b} type="button" className={band === b ? 'active' : ''} onClick={() => setBand(b)}>{b}</button>
                  ))}
                </div>
              )}
            >
              <DataTable rows={employees} columns={EMPLOYEE_COLUMNS} maxRows={30} emptyText="No employees in this band." />
            </Section>
          </>
        )
      }}
    </ManagementReportShell>
  )
}

export default ResourceCapacityReport
